import { useEffect, useRef, useState } from "react";
import { getLocale, useLocaleStore, type Locale } from "../i18n/index.js";
import { useReducedMotion } from "../units/useReducedMotion.js";

export function formatCompact(n: number, locale: Locale = getLocale()): string {
  const abs = Math.abs(n);
  if (!Number.isFinite(n) || Math.round(abs) < 1000) return new Intl.NumberFormat(locale).format(Math.round(n));
  let unit = Math.min(3, Math.floor(Math.log10(Math.round(abs)) / 3));
  let rounded = Math.round(abs / 1000 ** unit * 10) / 10;
  if (rounded >= 1000 && unit < 3) { unit++; rounded = 1; }
  return `${new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(Math.sign(n) * rounded)}${["", "K", "M", "B"][unit]}`;
}

export interface CounterProps {
  value: number;
  compact?: boolean;
  durationMs?: number;
  className?: string;
}

/** Animated count-up/down to the target value (reward juice, R93). */
export function Counter({ value, compact = false, durationMs = 500, className = "" }: CounterProps) {
  const [shown, setShown] = useState(value);
  const current = useRef(value);
  const locale = useLocaleStore((state) => state.locale);
  const reduced = useReducedMotion();
  const instant = reduced || durationMs <= 0 || !Number.isFinite(durationMs);
  useEffect(() => {
    if (instant || current.current === value) {
      current.current = value;
      setShown(value);
      return;
    }
    const from = current.current;
    const started = performance.now();
    let frame = 0;
    const step = (t: number) => {
      const p = Math.max(0, Math.min(1, (t - started) / durationMs));
      const eased = 1 - Math.pow(1 - p, 3);
      current.current = from + (value - from) * eased;
      setShown(current.current);
      if (p < 1) frame = requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [value, durationMs, instant]);
  const displayed = instant ? value : shown;
  return <span className={`mono ${className}`}>{compact ? formatCompact(displayed, locale) : new Intl.NumberFormat(locale).format(Math.round(displayed))}</span>;
}
